import Fuse, { FuseResult } from 'fuse.js';
import { normalizeSearchField } from './clinical-timeline-search-normalize';
import { createTimelineFuseOptions, maxFuseScoreForQuery } from './clinical-timeline-search-tuning';
import type { ClinicalTimelineSearchIndexRecord, ClinicalTimelineSearchRawItem } from './clinical-timeline-search-types';

export type { ClinicalTimelineSearchIndexRecord, ClinicalTimelineSearchRawItem } from './clinical-timeline-search-types';

export function normalizeRawItem(raw: ClinicalTimelineSearchRawItem): ClinicalTimelineSearchIndexRecord {
  return {
    id: raw.id,
    title: normalizeSearchField(raw.title, !!raw.titleMd),
    body: normalizeSearchField(raw.body, !!raw.bodyMd),
    footer: normalizeSearchField(raw.footer, false),
    heading: normalizeSearchField(raw.heading, false),
    dates: normalizeSearchField(raw.dates, false),
    diagnosis: normalizeSearchField(raw.diagnosis, false),
    summary: normalizeSearchField(raw.summary, false),
    hospital: normalizeSearchField(raw.hospital, false),
    discharge: normalizeSearchField(raw.discharge, !!raw.dischargeMd),
    text: normalizeSearchField(raw.text, false)
  };
}

export function buildNormalizedRecords(items: ClinicalTimelineSearchRawItem[]): ClinicalTimelineSearchIndexRecord[] {
  return items.map(normalizeRawItem);
}

export function createFuseIndex(records: ClinicalTimelineSearchIndexRecord[]): Fuse<ClinicalTimelineSearchIndexRecord> {
  return new Fuse(records, createTimelineFuseOptions());
}

export type ClinicalTimelineSearchHit = {
  id: string;
  record: ClinicalTimelineSearchIndexRecord;
  fuseResult: FuseResult<ClinicalTimelineSearchIndexRecord>;
};

export function searchTimelineCorpus(
  fuse: Fuse<ClinicalTimelineSearchIndexRecord> | null,
  query: string
): ClinicalTimelineSearchHit[] {
  if (!fuse || !query.trim()) {
    return [];
  }
  const trimmed = query.trim();
  const scoreCap = maxFuseScoreForQuery(trimmed);
  const results = fuse.search(trimmed).filter((r) => (r.score ?? 1) <= scoreCap);
  return results.map((fuseResult) => ({
    id: fuseResult.item.id,
    record: fuseResult.item,
    fuseResult
  }));
}
